import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import setWith from 'lodash/setWith';
import { SuiTextInput } from '@splashthat/sui';
import { useEditor } from '@splashthat/splash-cms-engine';
import { getCanUserAccessCMSCodeEditor } from 'common/store/permissions';
import Spacer from 'common/components/Spacer';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';

const CUSTOM_CLASSNAME_LABEL = 'CSS Classes';

const CUSTOM_CSS_PROP_PATH = 'styles';

export const CUSTOM_CSS_PROP_NAME = 'customCssClassName';

/**
 * This function returns custom CSS class name
 */
export const getCustomCssClassName = (node, path) => {
    if (!node) return undefined;

    const customCssClassName = get(node, path || `${CUSTOM_CSS_PROP_PATH}.${CUSTOM_CSS_PROP_NAME}`);
    return customCssClassName;
};

/**
 * This component is handling Custom CSS Name text box
 */
export default function CustomCssTools({ customCssClassNamePath, QaBaseClassName, spacer }) {
    const { event } = useTouchpointData();
    const { actions, ...node } = useEditor((state) => state.nodes[state.events.selected]);

    // Check if access to the custom CSS tool is not allowed.
    const canUserAccessCMSCodeEditor = useSelector(getCanUserAccessCMSCodeEditor);
    if (!canUserAccessCMSCodeEditor || event?.touchpoint !== 'agenda' || !actions || !node) {
        return null;
    }

    const customCssClassName = `${customCssClassNamePath}.${CUSTOM_CSS_PROP_NAME}`;
    const customCssClassNameValue = get(node, customCssClassName);

    const onChangeCustomCssClassNameHandler = (_, className) =>
        actions.setNodeProps(node.id, (editedNode) => {
            setWith(editedNode, customCssClassName, className, (obj) => {
                if (obj === undefined || (Array.isArray(obj) && !obj.length)) {
                    return {};
                }
                return obj;
            });
            return editedNode;
        });

    return (
        <>
            {(spacer === 'before' || spacer === 'both') && <Spacer />}
            <SuiTextInput
                label={CUSTOM_CLASSNAME_LABEL}
                value={customCssClassNameValue}
                onChange={onChangeCustomCssClassNameHandler}
                data-qa={`${QaBaseClassName || node?.id}__customCssClasses-text-input`}
                fullWidth
            />
            {(spacer === 'after' || spacer === 'both') && <Spacer />}
        </>
    );
}

CustomCssTools.propTypes = {
    customCssClassNamePath: PropTypes.string,
    QaBaseClassName: PropTypes.string,
    spacer: PropTypes.oneOf(['none', 'before', 'after', 'both']),
};

CustomCssTools.defaultProps = {
    customCssClassNamePath: CUSTOM_CSS_PROP_PATH,
    spacer: 'none',
};
